import React from 'react'

const DataProcessingPolicy = () => {
  const PolicySection = ({ title, content }) => (
    <section className="mb-10 pb-10 border-b border-gray-200 last:border-b-0">
      <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-4 tracking-tight">
        {title}
      </h2>
      <div className="text-gray-800 text-sm sm:text-base leading-relaxed space-y-3">
        {content}
      </div>
    </section>
  )

  return (
    <div className="font-nanum-myeongjo min-h-screen bg-white px-4 py-8 sm:px-6 sm:py-12 lg:px-8 lg:py-16">
      <div className="mx-auto max-w-4xl">
        <div className="border-b-2 border-gray-300 pb-8 text-center sm:pb-12 lg:pb-16">
          <p className="mb-3 text-sm font-semibold uppercase tracking-[0.3em] text-yellow-600">VRIFY Technologies</p>
          <h1 className="mb-4 text-3xl font-light tracking-tight text-gray-900 sm:text-4xl md:text-5xl">
            Data Processing Policy
          </h1>
          <p className="mx-auto max-w-2xl text-sm leading-7 text-gray-700 sm:text-base">
            This policy sets out how VRIFY Technologies collects, uses, stores, protects, and shares personal data in Ghana and across international markets.
          </p>
          <p className="mt-3 text-xs text-gray-500 sm:text-sm">Last Updated: May 2026</p>
        </div>

        <PolicySection
          title="1. Scope and purpose"
          content={
            <div className="space-y-3">
              <p>
                VRIFY Technologies (“we”, “our”, or “us”) provides immersive technology services, including virtual reality, augmented reality, virtual tours, web development, and related business support.
              </p>
              <p>
                This policy explains how we process personal data when you visit our website, request a quote, submit an inquiry, use our digital services, or interact with our immersive solutions.
              </p>
              <p>
                This policy is intended to be clear, practical, and aligned with Ghana’s legal requirements and internationally recognized privacy principles.
              </p>
            </div>
          }
        />

        <PolicySection
          title="2. Governing law and international privacy principles"
          content={
            <div className="space-y-3">
              <p>
                In Ghana, the principal legal framework governing personal data processing is the <strong>Data Protection Act, 2012 (Act 843)</strong>. The Act is administered by the <strong>Data Protection Commission (DPC)</strong>.
              </p>
              <p>
                We also align our internal processes with internationally accepted privacy principles commonly used in global privacy frameworks, including the <strong>GDPR and UK GDPR principles</strong> of lawfulness, fairness, transparency, purpose limitation, data minimization, accuracy, storage limitation, integrity and confidentiality, and accountability.
              </p>
              <p>
                Where data is transferred outside Ghana, we use safeguards appropriate to the destination country and the nature of the data, and we remain committed to protecting the privacy of individuals.
              </p>
            </div>
          }
        />

        <PolicySection
          title="3. What personal data we may collect"
          content={
            <div className="space-y-3">
              <p>Depending on the service you use, we may collect the following categories of data:</p>
              <ul className="list-disc space-y-2 pl-5">
                <li>Identity and contact information, such as name, email address, phone number, company name, and job title</li>
                <li>Project and service information, such as requirements, timelines, budget range, event details, and preferred services</li>
                <li>Website and digital analytics data, including IP address, device information, browser type, referral source, and usage patterns</li>
                <li>VR and AR interaction data, including session timing, navigation patterns, selected settings, and technical performance data</li>
                <li>Location or sensor data where required for a specific AR or location-based service</li>
                <li>Data submitted through forms, emails, customer support channels, and sales communications</li>
              </ul>
              <p>
                We do not collect more data than is reasonably necessary for the stated purpose, and we apply data minimization by default.
              </p>
            </div>
          }
        />

        <PolicySection
          title="4. Lawful bases for processing"
          content={
            <div className="space-y-3">
              <p>We process personal data only where one or more lawful bases apply, including:</p>
              <ul className="list-disc space-y-2 pl-5">
                <li><strong>Consent:</strong> where you have voluntarily provided information and agreed to the processing for a specific purpose</li>
                <li><strong>Contractual necessity:</strong> where processing is required to deliver a requested service, quote, or contract</li>
                <li><strong>Legitimate interests:</strong> where processing is necessary for business operations, fraud prevention, service improvement, or security</li>
                <li><strong>Legal obligation:</strong> where we are required to retain or disclose data by law or regulation</li>
              </ul>
              <p>
                We will explain the relevant lawful basis in a clear way where it is practical and relevant to do so.
              </p>
            </div>
          }
        />

        <PolicySection
          title="5. How we process VR, AR, and virtual tour data"
          content={
            <div className="space-y-3">
              <p>
                For VR and immersive content experiences, we may process interaction data such as session duration, navigation patterns, selected content, headset or device details, and technical performance metrics.
              </p>
              <p>
                For AR experiences, we may process device and sensor information, including location data where needed for location-based features. Where possible, we process sensor or camera data locally on the user device and avoid unnecessary storage.
              </p>
              <p>
                For virtual tours, we may process engagement data such as route taken, time spent on each section, interactions with hotspots, and general usage patterns to improve the experience and support analytics.
              </p>
              <p>
                We do not use immersive interaction data to make automated decisions that materially affect an individual unless clearly disclosed and legally justified.
              </p>
            </div>
          }
        />

        <PolicySection
          title="6. How we process contact, quote, and business inquiry data"
          content={
            <div className="space-y-3">
              <p>
                When you use our contact form, quotation form, or email channels, we process your information to respond to your request, assess your needs, prepare proposals, and manage our business relationship.
              </p>
              <p>
                Information we may collect includes your name, contact details, organization, project description, service preferences, timeline, and any supporting information you provide.
              </p>
              <p>
                We keep these records only for as long as required for the relevant business purpose, legal compliance, or dispute management.
              </p>
              <p>
                We do not sell your personal data. We do not disclose your data to marketing companies without your permission.
              </p>
            </div>
          }
        />

        <PolicySection
          title="7. Purpose limitation and retention"
          content={
            <div className="space-y-3">
              <p>
                We collect data only for stated, transparent purposes, including service delivery, support, security, analytics, and legal compliance.
              </p>
              <p>
                Retention periods are based on the purpose of processing and any applicable legal or contractual requirement. In general:
              </p>
              <ul className="list-disc space-y-2 pl-5">
                <li>Contact and support records are retained for as long as needed to respond to the inquiry and satisfy legal or business records requirements</li>
                <li>Quote and sales records are retained for business records and contract-related purposes</li>
                <li>Analytics and usage logs are retained for a limited period and then deleted or anonymized where possible</li>
                <li>Data that is no longer needed is securely deleted or anonymized</li>
              </ul>
              <p>
                If you request deletion, we will assess whether there is a legal or regulatory reason that requires us to keep the data and will explain that decision where required.
              </p>
            </div>
          }
        />

        <PolicySection
          title="8. Security controls"
          content={
            <div className="space-y-3">
              <p>We apply the following security controls to protect personal data:</p>
              <ul className="list-disc space-y-2 pl-5">
                <li>Encryption in transit and at rest for data handled by our systems</li>
                <li>Role-based access control and restricted administrative access</li>
                <li>Monitoring, logging, and incident detection for suspicious activity</li>
                <li>Regular vulnerability assessment, patch management, and internal review of systems</li>
                <li>Secure backup, recovery planning, and incident response procedures</li>
                <li>Contractual security obligations imposed on third-party processors</li>
              </ul>
              <p>
                We take reasonable technical and organizational measures to reduce the risk of unauthorized access, loss, disclosure, or alteration of personal data.
              </p>
            </div>
          }
        />

        <PolicySection
          title="9. Third-party processors and cross-border transfers"
          content={
            <div className="space-y-3">
              <p>
                We may use third-party service providers for hosting, analytics, customer communication, payment processing, customer relationship management, and related business functions.
              </p>
              <p>
                We require those processors to process data only for the purposes we specify and to maintain appropriate security controls. Where cross-border transfers are involved, we assess the destination, the transfer mechanism, and the required safeguards before sharing personal data.
              </p>
              <p>
                We do not sell personal data to third parties for commercial gain.
              </p>
            </div>
          }
        />

        <PolicySection
          title="10. Cookies and analytics"
          content={
            <div className="space-y-3">
              <p>
                Our website may use cookies and similar technologies for essential functionality, security, analytics, and performance monitoring.
              </p>
              <p>
                Where non-essential cookies are used for analytics or marketing, we will disclose that purpose and seek consent where required by applicable law.
              </p>
              <p>
                You can manage cookies through your browser settings, although disabling some cookies may affect certain website features.
              </p>
            </div>
          }
        />

        <PolicySection
          title="11. Children’s data"
          content={
            <div className="space-y-3">
              <p>
                Our services are not intended for children without appropriate parental or guardian consent, and we do not knowingly collect personal data from children in a way that is inconsistent with applicable law.
              </p>
              <p>
                If we become aware that we have received personal data from a child without the required consent, we will delete the data as soon as practicable and notify the relevant parent or guardian where appropriate.
              </p>
            </div>
          }
        />

        <PolicySection
          title="12. Your rights under Ghana law and internationally recognized privacy principles"
          content={
            <div className="space-y-3">
              <p>
                Subject to applicable law, individuals may have rights including the right to access, correct, delete, restrict, and object to the processing of their personal data.
              </p>
              <ul className="list-disc space-y-2 pl-5">
                <li>Access to the personal data we hold about you</li>
                <li>Correction of inaccurate or incomplete data</li>
                <li>Deletion of data where the processing is no longer justified</li>
                <li>Restriction of processing in certain circumstances</li>
                <li>Portability of data in a structured format where applicable</li>
                <li>Withdrawal of consent where processing depends on consent</li>
              </ul>
              <p>
                To exercise any of these rights, please contact us at <strong>info@vrifytechnologies.com</strong>. We will respond within a reasonable time and in accordance with applicable legal requirements.
              </p>
            </div>
          }
        />

        <PolicySection
          title="13. How to contact us"
          content={
            <div className="space-y-3">
              <p>
                If you have questions about this policy, would like to exercise your data rights, or wish to raise a privacy concern, please contact us at:
              </p>
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm sm:text-base">
                <p><strong>Email:</strong> info@vrifytechnologies.com</p>
                <p><strong>Phone:</strong> +233 553 139 500</p>
                <p><strong>Address:</strong> Nii Sai Road, East Legon, Accra, Ghana</p>
              </div>
              <p>
                If you believe your rights under Ghana’s data protection laws have been violated, you may also contact the Data Protection Commission in Ghana.
              </p>
            </div>
          }
        />

        <PolicySection
          title="14. Changes to this policy"
          content={
            <div className="space-y-3">
              <p>
                We may update this policy from time to time to reflect changes in our services, technology, legal obligations, or industry best practices.
              </p>
              <p>
                When a material change is made, we will update the “Last Updated” date and publish the revised policy on our website.
              </p>
            </div>
          }
        />

        <div className="mt-8 rounded-xl border border-gray-200 bg-white p-5 text-sm leading-7 text-gray-700 sm:text-base">
          <p>
            <strong>Important note:</strong> This policy is provided for information and is not legal advice. If your organization requires specific legal review for a particular jurisdiction or contract, you should seek qualified legal counsel.
          </p>
        </div>
      </div>
    </div>
  )
}

export default DataProcessingPolicy